import { Table } from "../../../../components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import {
  AddressMode,
  prepareAddressForForm,
} from "../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { AddressValue } from "./types";
import { useTranslation } from "react-i18next";
import { DiffText } from "./styles";

const EMPTY_VALUE = "-";

interface AddressRow {
  attribute: string;
  before: string;
  after: string;
}

interface AddressTableProps {
  before?: AddressValue | null;
  after?: AddressValue | null;
  addressMode?: AddressMode;
}

const getValue = (obj: Record<string, any> | null, key: string): string => {
  const val = obj?.[key];
  if (!val) return EMPTY_VALUE;
  if (typeof val === "object" && "label" in val) return val.label;
  return String(val);
};

export const getStandardAddressFields = (t: (key: string) => string) => [
  { label: t("Company"), key: "company_name" },
  { label: t("Address Line 1"), key: "address1" },
  { label: t("Address Line 2 (Optional)"), key: "address2" },
  { label: t("Address Line 3 (Optional)"), key: "address3" },
  { label: t("Country"), key: "country" },
  { label: t("State"), key: "state" },
  { label: t("City"), key: "city" },
  { label: t("Postal Code"), key: "postal_code" },
];

export const getAdvancedAddressFields = (t: (key: string) => string) => [
  ...getStandardAddressFields(t),
  { label: t("CRM ID"), key: "crm_id" },
  { label: t("Tax ID"), key: "tax_id" },
  { label: t("Location ID"), key: "location_id" },
  { label: t("First Name"), key: "poc_first_name" },
  { label: t("Last Name"), key: "poc_last_name" },
  { label: t("Email"), key: "poc_email" },
  { label: t("Country Code"), key: "poc_country_code" },
  { label: t("Phone Number"), key: "poc_phone" },
];

export const getAddressFieldsByMode = (
  t: (key: string) => string,
  addressMode: AddressMode = "standard"
) => {
  return addressMode === "standard"
    ? getStandardAddressFields(t)
    : getAdvancedAddressFields(t);
};

export function AddressTable({
  before,
  after,
  addressMode = "standard",
}: AddressTableProps) {
  const { t } = useTranslation();
  const preparedBefore = before ? prepareAddressForForm(before) : null;
  const preparedAfter = after ? prepareAddressForForm(after) : null;
  const rows = getAddressFieldsByMode(t, addressMode);

  const tableData: AddressRow[] = rows.map(({ label, key }) => ({
    attribute: label,
    before: getValue(preparedBefore, key),
    after: getValue(preparedAfter, key),
  }));

  const columns: ColumnDef<AddressRow>[] = [
    {
      header: t("Address Attributes"),
      accessorKey: "attribute",
    },
    {
      header: t("Before"),
      accessorKey: "before",
      cell: ({ row }) => {
        const before = row.original.before;
        const after = row.original.after;
        const hasChange = before !== after && before !== EMPTY_VALUE;

        if (before === EMPTY_VALUE) return <span>{EMPTY_VALUE}</span>;
        return <DiffText removed={hasChange}>{before}</DiffText>;
      },
    },
    {
      header: t("After"),
      accessorKey: "after",
      cell: ({ row }) => {
        const before = row.original.before;
        const after = row.original.after;
        const hasChange = before !== after && after !== EMPTY_VALUE;

        if (after === EMPTY_VALUE) return <span>{EMPTY_VALUE}</span>;
        return <DiffText added={hasChange}>{after}</DiffText>;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      data={tableData}
      isLoading={false}
      error={undefined}
      enableDefaultSort={false}
      enableColumnResize={false}
      lastChildleftAlign={true}
      cellVerticalAlignTop={true}
    />
  );
}
